import { useState, useSyncExternalStore } from 'react';
import { useChatStore } from '../../../store/chatStore';
import { Check, Copy, MoreVertical, Reply, RotateCcw, Smile } from '../../../icons';
import { AiAvatar } from '../../../components/AiAvatar';
import type { ChatMessageEvent } from '../../../types/chat';
import { AttachmentList } from '../../../components/Attachment';
import { formatMessageTime } from './utils/formatMessageTime';
import { renderMessageMarkdown } from '../../../lib/chat-markdown';
import { ReplyQuote } from './ReplyQuote';
import { ReactionBar } from './ReactionBar';
import { EmojiPicker } from './EmojiPicker';
import { toggleReaction } from '../../../api/chat';
import { tChat, getChatLang, subscribeChatLang } from '../../../lib/chat-i18n';

interface AiTextMessageProps {
  message: ChatMessageEvent;
  showAvatar: boolean;
}

/**
 * AiTextMessage (P2-16d) — AI 일반 텍스트 응답 (`is_ai && !ai_card_type`).
 *
 * 시안 line 1166~1206 의 AI 메시지 부분을 캡슐화.
 *   - 그라데이션 Bot 아바타 (radius 10px) — 일반 사용자 (radius 50%) 와 시각 구분
 *   - 말풍선 보더: primary-40 (옅은 primary) — `chat-message-bubble--ai`
 *   - hover 액션 바: Reply · Copy · **RotateCcw (AI 전용 — 다시 답변 요청)** · Smile · More
 *   - sender 라벨 옆 'AI' 뱃지
 *
 * 일반 텍스트 + isManualCard 인 경우는 분기 — manual card 는 P2-16h, action 카드는 P2-16e.
 * 본 컴포넌트는 일반 AI 텍스트 응답 (`aiCardType === null`) 만 책임.
 */
export function AiTextMessage({ message, showAvatar }: AiTextMessageProps) {
  useSyncExternalStore(subscribeChatLang, getChatLang, getChatLang);
  // hover 툴바 표시는 CSS :hover 로 처리 — 전역 hoveredMsgId 구독을 제거해 hover 시
  // 모든 메시지가 리렌더(markdown/mermaid 재렌더 → 깜빡임)되던 문제 해소.
  const setReplyingTo = useChatStore((s) => s.setReplyingTo);
  const reactionOverride = useChatStore((s) => s.reactionsByMessageId[message.id]);
  const applyMyReaction = useChatStore((s) => s.applyMyReaction);
  const reactions = reactionOverride ?? message.reactions ?? [];
  const [copied, setCopied] = useState(false);
  const [showReactPicker, setShowReactPicker] = useState(false);
  const handleCopy = () => {
    void navigator.clipboard?.writeText(message.content);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };
  const handleReact = async (emoji: string) => {
    setShowReactPicker(false);
    try {
      const res = await toggleReaction(message.roomId, message.id, emoji);
      applyMyReaction(message.id, reactions, emoji, res.action);
    } catch { /* best-effort */ }
  };
  const time = formatMessageTime(message.createdAt);

  return (
    <div
      className={`chat-message-row${showAvatar ? ' chat-message-row--show-avatar' : ' chat-message-row--continuation'}`}
      /* data-message-id — 대화 검색 결과에서 해당 메시지로 뛰는 앵커(ChatDetailPanel.jumpTo). */
      data-message-id={message.id}
    >
      <div className="chat-message-avatar-slot">
        {showAvatar && (
          <div className="chat-message-avatar chat-message-avatar--ai" aria-label={tChat('chat.item.ai_assistant')}>
            <AiAvatar />
          </div>
        )}
      </div>
      <div className="chat-message-body">
        {showAvatar && (
          <div className="chat-message-meta">
            <span className="chat-message-sender">{tChat('chat.item.ai_assistant')}</span>
            <span className="chat-message-ai-badge">{tChat('chat.item.ai_badge')}</span>
            {time && <span className="chat-message-time">{time}</span>}
          </div>
        )}
        <ReplyQuote roomId={message.roomId} replyToId={message.replyToId} />
        <div className="chat-message-bubble-wrap">
          <div className="chat-message-bubble chat-message-bubble--ai">{renderMessageMarkdown(message.content)}</div>
          {(
            <div className={`chat-message-hover-actions${showReactPicker ? ' is-pinned' : ''}`} role="toolbar" aria-label={tChat('chat.ai_text.aria_hover_toolbar')}>
              <button type="button" className="chat-message-hover-action" onClick={() => setReplyingTo(message.roomId, message)} aria-label={tChat('chat.item.aria_reply')}>
                <Reply size={13} />
              </button>
              <button type="button" className="chat-message-hover-action" onClick={handleCopy} aria-label={copied ? '복사됨' : tChat('chat.item.aria_copy')}>
                {copied ? <Check size={13} /> : <Copy size={13} />}
              </button>
              <button type="button" className="chat-message-hover-action" aria-label={tChat('chat.ai_text.aria_retry')} disabled>
                <RotateCcw size={13} />
              </button>
              <div className="chat-message-react-wrap">
                <button type="button" className="chat-message-hover-action" onClick={() => setShowReactPicker((v) => !v)} aria-label={tChat('chat.item.aria_emoji')} aria-expanded={showReactPicker}>
                  <Smile size={13} />
                </button>
                {showReactPicker && <EmojiPicker onSelect={handleReact} onClose={() => setShowReactPicker(false)} />}
              </div>
              <button type="button" className="chat-message-hover-action" aria-label={tChat('chat.item.aria_more')} disabled>
                <MoreVertical size={13} />
              </button>
            </div>
          )}
        </div>
        <AttachmentList attachments={message.attachments} roomId={message.roomId} />
        <ReactionBar reactions={reactions} onToggle={handleReact} />
      </div>
    </div>
  );
}
